import { Button, Flex } from "@chakra-ui/react";
import { Typography, Modal } from "@reconvert/react-ui-component";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import useSlots from "../../hooks/useSlots";
import useVariants from "../../hooks/useVariants";
import { Platform, Slot } from "../../models";
import { EmptyState } from "../EmptyState";
import SlotHeader from "../SlotHeader/SlotHeader";
import VariantCard from "../VariantCard";
import { getPreMadeTemplates } from "../PreMadeTemplateModal/PreMadeTemplateModal";
import { useDirection } from "../../hooks/useDirection";
import { UNLEASH_ENABLE_ANNOUNCEMENT_BAR } from "../../utils/constant";
import useIsActiveOnPage, { PagePurpose } from "../../hooks/useIsActiveOnPage";
import { useFlag } from "@unleash/proxy-client-react";
import { useAppSelector } from "../../app/hooks";
import { selectAppState } from "../../app/slice/appSlice";
import { APP_PLATFORM } from "../../config";
import { PageType } from "@reconvert/reconvert-utils";

const SlotVariant = ({ slot, variants }: { slot: Slot; variants: any }) => {
  const { openDeleteSlotModal, setOpenDeleteSlotModal, handleSlotDelete, deletingSlot, isPreMadeTemplateSlot } = useSlots();
  const { creatingVariant } = useVariants();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { activePage } = useAppSelector(state => state.app);
  const { storeInfo } = useAppSelector(selectAppState);
  const isAnnouncementBarEnabled = useFlag(UNLEASH_ENABLE_ANNOUNCEMENT_BAR);
  const isAnnouncementBarAccessible = useIsActiveOnPage(
    PagePurpose.ANNOUNCEMENT_BAR_ACCESSIBLE,
    activePage!,
    isAnnouncementBarEnabled,
  );
  const isTemplateSlot = isPreMadeTemplateSlot(slot.slotNo, slot.type);

  const templateSlotName = getPreMadeTemplates(slot.type, isAnnouncementBarAccessible).find(a => a.slotNo === slot.slotNo)?.title;

  const handleEditorRedirect = () => {
    const editorUrl = `https://manage.wix.com/editor/${storeInfo?.siteId}`;

    window.open(editorUrl, "_blank");
  };

  const handleAlreadyDeleted = () => {
    handleSlotDelete(slot.slotNo);
  };

  const isWixProductPage = APP_PLATFORM === Platform.Wix && activePage === PageType.PRE_PURCHASE_PRODUCT_PAGE;

  return (
    <>
      {!variants?.length ? (
        <EmptyState slot={slot} isLoading={creatingVariant} onSlotDelete={() => setOpenDeleteSlotModal(true)} />
      ) : (
        <Flex direction={"column"}>
          <SlotHeader slot={slot} />
          <>
            {variants[0]?.widgets?.length ? (
              <VariantCard variant={variants?.[0]} slot={slot} />
            ) : (
              <EmptyState
                onSlotDelete={() => setOpenDeleteSlotModal(true)}
                hideHeader={true}
                slot={slot}
                isLoading={creatingVariant}
              />
            )}
          </>
        </Flex>
      )}
      <Modal
        title={
          isTemplateSlot
            ? t("Are you sure you want to delete {{name}}?", { name: t(templateSlotName || "") })
            : `${t("Are you sure you want to delete slot")} ${slot.slotNo} - ${slot.name}?`
        }
        okButton={{
          text: isTemplateSlot ? `${t("Delete")} ${templateSlotName ? t(templateSlotName) : ""}` : `${t("Delete Slot")} ${slot.slotNo}`,
          onClick: () => handleSlotDelete(slot.slotNo),
          variant: "destructive",
          isLoading: deletingSlot,
        }}
        cancelButton={{
          text: t("Cancel"),
          onClick: () => setOpenDeleteSlotModal(false),
          variant: "secondary",
        }}
        isOpen={openDeleteSlotModal}
        direction={direction}
        size={"2xl"}
        onClose={() => setOpenDeleteSlotModal(false)}
        footer={
          isWixProductPage ? (
            <Flex gap="8px" justifyContent="flex-end" marginTop={'24px'} marginBottom={'24px'} marginLeft={'24px'} paddingRight={'24px'}>
              <Button
                padding={"12px 16px"}
                variant={"ghost"}
                onClick={() => setOpenDeleteSlotModal(false)}
              >
                <Typography variant="body-16-regular">
                  {t("Cancel")}
                </Typography>
              </Button>
              <Button
                padding={"12px 16px"}
                variant={"secondary"}
                onClick={handleAlreadyDeleted}
                isLoading={deletingSlot}
              >
                <Typography variant="body-16-regular">
                  {t("I already deleted slot")}
                </Typography>
              </Button>
              <Button
                padding={"12px 16px"}
                variant={"solid"}
                onClick={handleEditorRedirect}
              >
                <Typography variant="body-16-regular">
                  {t("Go to Wix Editor")}
                </Typography>
              </Button>
            </Flex>
          ) : undefined
        }
      >
        {isWixProductPage ?
          <Typography variant="body-16-regular">
            {t("To delete this slot, please remove it from the Product page in the Wix Editor first. If you have already removed it there, you can delete it here")}
          </Typography>
          :
          <Typography variant="body-16-regular">
            {t("If you delete this slot, it will be removed from")} <strong>{t("all")}</strong>{" "}
            {t("conditions that use it.")}
          </Typography>}
      </Modal>
    </>
  );
};

export default React.memo(SlotVariant);
